import { DEFAULT_RANGE } from '../constants'
import type {
  PlayerProgress,
  ProgressBucket,
  ProgressComparison,
  ProgressPeriod,
  StatValue,
} from '../types'

const BUCKETS: ProgressBucket[] = ['day', 'week', 'month']
const DEFAULT_BUCKET: ProgressBucket = 'week'
/** A period's start and end are calendar dates. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Stands in for a missing `this_week`: no dates, no games. */
const EMPTY_WEEK: ProgressPeriod = {
  period_start: '',
  period_end: '',
  games_played: 0,
  stats: [],
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isCalendarDate = (value: unknown): value is string =>
  typeof value === 'string' && CALENDAR_DATE.test(value)

/** A period with a calendar start, its gaps filled; null when it has none. */
const normalizePeriod = (raw: unknown): ProgressPeriod | null => {
  if (!isRecord(raw) || !isCalendarDate(raw.period_start)) {
    return null
  }
  return {
    period_start: raw.period_start,
    period_end: isCalendarDate(raw.period_end)
      ? raw.period_end
      : raw.period_start,
    games_played: typeof raw.games_played === 'number' ? raw.games_played : 0,
    stats: Array.isArray(raw.stats) ? (raw.stats as StatValue[]) : [],
  }
}

const normalizeComparison = (raw: unknown): ProgressComparison | null => {
  if (!isRecord(raw)) {
    return null
  }
  const current = normalizePeriod(raw.current)
  return current ? { current, previous: normalizePeriod(raw.previous) } : null
}

/**
 * The progress response with every field the Stats tab reads guaranteed:
 * an empty week for a missing `this_week`, no periods for a missing `series`,
 * periods without a calendar start dropped, and a comparison without a
 * current window removed. A gap in the response shows as missing data, never
 * as a crash.
 */
export const normalizeProgress = (raw: unknown): PlayerProgress => {
  const data = isRecord(raw) ? raw : {}
  // The fields the tab only passes along keep their type from the API.
  const given = data as Partial<PlayerProgress>
  const series = Array.isArray(data.series) ? data.series : []
  return {
    sport: given.sport ?? null,
    generic_sport: given.generic_sport ?? null,
    goalie: given.goalie ?? null,
    range: given.range ?? DEFAULT_RANGE,
    timezone: given.timezone ?? '',
    bucket: BUCKETS.includes(data.bucket as ProgressBucket)
      ? (data.bucket as ProgressBucket)
      : DEFAULT_BUCKET,
    streak_weeks: typeof data.streak_weeks === 'number' ? data.streak_weeks : 0,
    this_week: normalizePeriod(data.this_week) ?? EMPTY_WEEK,
    series: series.flatMap((period) => {
      const normalized = normalizePeriod(period)
      return normalized ? [normalized] : []
    }),
    comparison: normalizeComparison(data.comparison),
  }
}
